import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Package } from '../../types/package';
import { usePackagesStore } from '../../stores/packagesStore';
import { Edit, Trash2, Download, Search, ChevronLeft, ChevronRight, Building as Buildings, Calendar, Hotel, Building2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { exportPackagesToExcel } from '../../utils/exportPackages';
import toast from 'react-hot-toast';

type Props = {
  packages: Record<string, Package>;
  onEditPackage: (packageId: string) => void;
};

export const PackagesTable = ({ packages, onEditPackage }: Props) => {
  const { deletePackage } = usePackagesStore();
  const { translations, direction } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Filter packages based on search term
  const filteredPackages = useMemo(() => {
    return Object.values(packages).filter(pkg => {
      if (!searchTerm) return true;
      
      const searchValue = searchTerm.toLowerCase();
      return (
        pkg.name.toLowerCase().includes(searchValue) ||
        pkg.mainMakkahHotel.toLowerCase().includes(searchValue) ||
        (pkg.shiftingMakkahHotel && pkg.shiftingMakkahHotel.toLowerCase().includes(searchValue)) ||
        pkg.madinahHotel.toLowerCase().includes(searchValue) ||
        pkg.packageType.toLowerCase().includes(searchValue) ||
        pkg.camp.toLowerCase().includes(searchValue)
      );
    });
  }, [packages, searchTerm]);
  
  // Sort packages by creation date (newest first)
  const sortedPackages = useMemo(() => {
    return [...filteredPackages].sort((a, b) => b.createdAt - a.createdAt);
  }, [filteredPackages]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedPackages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPackages = sortedPackages.slice(startIndex, startIndex + itemsPerPage);

  const handleDeletePackage = async (packageId: string) => {
    if (!confirm(translations?.packages?.confirmDelete || 'Are you sure you want to delete this package?')) {
      return;
    }

    try {
      await deletePackage(packageId);
      toast.success(translations?.packages?.deleteSuccess || 'Package deleted successfully');
    } catch (error: any) {
      toast.error(error.message || translations?.packages?.deleteError || 'Failed to delete package');
    }
  };

  const handleExportAll = () => {
    const success = exportPackagesToExcel(sortedPackages);
    if (success) {
      toast.success(translations?.packages?.exportSuccess || 'Packages exported successfully');
    } else {
      toast.error(translations?.packages?.exportError || 'Failed to export packages');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header with Search and Export */}
      <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b">
        <div className="relative flex-grow max-w-md">
          <input
            type="text"
            placeholder={translations?.packages?.searchPlaceholder || "Search packages..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>
        <button
          onClick={handleExportAll}
          className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100"
        >
          <Download size={18} />
          <span>{translations?.packages?.exportAll || 'Export All'}</span>
        </button>
      </div>

      {/* Filter Stats */}
      {searchTerm && (
        <div className="px-4 py-2 bg-gray-50 border-b">
          <span className="text-sm text-gray-600">
            {filteredPackages.length} {translations?.packages?.matchingPackages || 'matching packages'}
          </span>
        </div>
      )}

      {/* Mobile View */}
      <div className="md:hidden">
        {paginatedPackages.map((pkg) => (
          <div key={pkg.id} className="p-4 border-b last:border-b-0 hover:bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-primary-50 rounded-lg">
                  <Buildings size={18} className="text-primary-500" />
                </div>
                <span className="font-medium">{pkg.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEditPackage(pkg.id)}
                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDeletePackage(pkg.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-2 mt-3">
              {/* Main Makkah */}
              <div className="bg-gray-50 p-2 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Building2 size={16} className="text-primary-500" />
                  <span>Main Makkah:</span>
                </div>
                <div className="text-sm">{pkg.mainMakkahHotel}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {pkg.mainMakkahStartDate} - {pkg.mainMakkahEndDate}
                </div>
              </div>
              
              {/* Shifting Makkah (if exists) */}
              {pkg.shiftingMakkahHotel && (
                <div className="bg-gray-50 p-2 rounded-lg">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                    <Building2 size={16} className="text-blue-500" />
                    <span>Shifting Makkah:</span>
                  </div>
                  <div className="text-sm">{pkg.shiftingMakkahHotel}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {pkg.shiftingMakkahStartDate} - {pkg.shiftingMakkahEndDate}
                  </div>
                </div>
              )}
              
              {/* Madinah */}
              <div className="bg-gray-50 p-2 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Building2 size={16} className="text-green-500" />
                  <span>Madinah:</span>
                </div>
                <div className="text-sm">{pkg.madinahHotel}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {pkg.madinahStartDate} - {pkg.madinahEndDate}
                </div>
              </div>
              
              {/* Package Details */}
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-1 bg-primary-50 text-primary-600 text-xs rounded-full">
                  {pkg.packageType}
                </span>
                <span className="px-2 py-1 bg-secondary-50 text-secondary-600 text-xs rounded-full">
                  {pkg.camp}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                direction === 'rtl' ? 'text-right' : 'text-left'
              }`}>
                {translations?.packages?.headers?.name || 'Package Name'}
              </th>
              <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                direction === 'rtl' ? 'text-right' : 'text-left'
              }`}>
                {translations?.packages?.headers?.makkah || 'Makkah Hotels'}
              </th>
              <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                direction === 'rtl' ? 'text-right' : 'text-left'
              }`}>
                {translations?.packages?.headers?.madinah || 'Madinah Hotel'}
              </th>
              <th className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                direction === 'rtl' ? 'text-right' : 'text-left'
              }`}>
                {translations?.packages?.headers?.type || 'Package Type'}
              </th>
              <th className={`px-6 py-3 text-right text-sm font-medium text-gray-500`}>
                {translations?.packages?.headers?.actions || 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedPackages.map((pkg) => (
              <tr key={pkg.id} className="hover:bg-gray-50">
                <td className={`px-6 py-4 ${
                  direction === 'rtl' ? 'text-right' : 'text-left'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-50 rounded-lg">
                      <Buildings size={18} className="text-primary-500" />
                    </div>
                    <span className="font-medium">{pkg.name}</span>
                  </div>
                </td>
                <td className={`px-6 py-4 ${
                  direction === 'rtl' ? 'text-right' : 'text-left'
                }`}>
                  <div className="space-y-2">
                    {/* Main Makkah */}
                    <div>
                      <div className="flex items-center gap-2">
                        <Hotel size={16} className="text-primary-500" />
                        <span className="font-medium">{pkg.mainMakkahHotel}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <Calendar size={14} />
                        <span>{pkg.mainMakkahStartDate} - {pkg.mainMakkahEndDate}</span>
                      </div>
                    </div>
                    
                    {/* Shifting Makkah (if exists) */}
                    {pkg.shiftingMakkahHotel && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <Hotel size={16} className="text-blue-500" />
                          <span className="font-medium">{pkg.shiftingMakkahHotel}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <Calendar size={14} />
                          <span>{pkg.shiftingMakkahStartDate} - {pkg.shiftingMakkahEndDate}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td className={`px-6 py-4 ${
                  direction === 'rtl' ? 'text-right' : 'text-left'
                }`}>
                  <div className="flex items-center gap-2">
                    <Hotel size={16} className="text-green-500" />
                    <span className="font-medium">{pkg.madinahHotel}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                    <Calendar size={14} />
                    <span>{pkg.madinahStartDate} - {pkg.madinahEndDate}</span>
                  </div>
                </td>
                <td className={`px-6 py-4 ${
                  direction === 'rtl' ? 'text-right' : 'text-left'
                }`}>
                  <div className="flex flex-col gap-2">
                    <span className="px-2 py-1 bg-primary-50 text-primary-600 text-xs rounded-full inline-block w-fit">
                      {pkg.packageType}
                    </span>
                    <span className="px-2 py-1 bg-secondary-50 text-secondary-600 text-xs rounded-full inline-block w-fit">
                      {pkg.camp}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEditPackage(pkg.id)}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title={translations?.packages?.edit || 'Edit'}
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDeletePackage(pkg.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={translations?.packages?.delete || 'Delete'}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
          <div className="flex items-center gap-4">
            {/* Items per page selector */}
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              className="px-2 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              {[5, 10, 25, 50].map(size => (
                <option key={size} value={size}>
                  {size} {translations?.pagination?.perPage || 'per page'}
                </option>
              ))}
            </select>

            {/* Pagination controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-1 rounded-lg ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {direction === 'ltr' ? (
                  <ChevronLeft size={20} />
                ) : (
                  <ChevronRight size={20} />
                )}
              </button>

              <span className="text-sm text-gray-700">
                {translations?.pagination?.page || 'Page'} {currentPage}{' '}
                {translations?.pagination?.of || 'of'} {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`p-1 rounded-lg ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {direction === 'ltr' ? (
                  <ChevronRight size={20} />
                ) : (
                  <ChevronLeft size={20} />
                )}
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {translations?.pagination?.showing || 'Showing'}{' '}
            {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedPackages.length)}{' '}
            {translations?.pagination?.of || 'of'} {sortedPackages.length}
          </div>
        </div>
      )}

      {/* Empty State */}
      {sortedPackages.length === 0 && (
        <div className="py-10 text-center">
          <Buildings className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchTerm ? 
              translations?.packages?.noMatchingPackages || 'No matching packages' :
              translations?.packages?.noPackages || 'No packages'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 
              translations?.packages?.tryDifferentSearch || 'Try a different search term' :
              translations?.packages?.getStarted || 'Get started by creating a new package'}
          </p>
        </div>
      )}
    </div>
  );
};